// Pro Manager direct-send email templates (Follow Up, Deposit Request, Booking Confirmed, Review Request)

export const BANK_DETAILS = {
  name: "HOMEGO REMOVALS & PACKING LTD",
  type: "Business",
  account: "57158107",
  sort: "23-05-80",
};

const firstName = (c) => (c.name || "there").split(" ")[0];
const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); } catch { return d; }
};

export const STATUS_EMAIL_MAP = {
  pending_decision: "follow_up",
  deposit_requested: "deposit_request",
  job_booked: "booking_confirmed",
  completed: "review_request",
};

export const PM_EMAIL_TEMPLATES = {
  follow_up: {
    label: "Follow Up — Quote Chase",
    banner: { gradient: "linear-gradient(135deg, #D4AF37 0%, #f4d03f 50%, #D4AF37 100%)", emoji: "👋", title: "Just Checking In!", subtitle: "Your quote is ready whenever you are", text: "#1a1a1a" },
    subject: (c) => `Just checking in on your removals quote, ${firstName(c)} — HomeGo Removals`,
    defaultMessage: (c) => `Hi ${firstName(c)},

I hope you're doing well! I just wanted to check in regarding the removals quote we sent you${c.quote_amount ? ` for £${c.quote_amount}` : ""}${c.move_date ? ` for your move on ${fmtDate(c.move_date)}` : ""}.

Is there anything you'd like me to clarify, or any way we can help make your decision easier? We're happy to adjust the package to suit your needs.

Remember — with HomeGo there are NO extra charges on the day. The price we quote is the price you pay, guaranteed.

Just reply to this email or give us a quick call, and we'll take care of the rest.`,
  },
  deposit_request: {
    label: "Deposit Request — Secure Booking",
    banner: { gradient: "linear-gradient(135deg, #F97316 0%, #fb923c 50%, #F97316 100%)", emoji: "🔒", title: "Secure Your Booking", subtitle: "A small deposit locks in your date & price", text: "#1a1a1a" },
    subject: (c) => `Secure your booking — deposit request | HomeGo Removals`,
    defaultMessage: (c) => `Hi ${firstName(c)},

Great news — we're ready to lock in your move${c.move_date ? ` on ${fmtDate(c.move_date)}` : ""}!

To secure your date and guarantee your quoted price${c.quote_amount ? ` of £${c.quote_amount}` : ""}, we just need a small deposit. Once received, your booking is fully confirmed and your slot is reserved exclusively for you.

You can pay by bank transfer using the details below. Please use your name as the payment reference, and reply to this email once sent so we can confirm straight away.`,
  },
  booking_confirmed: {
    label: "Booking Confirmed",
    banner: { gradient: "linear-gradient(135deg, #10B981 0%, #34d399 50%, #10B981 100%)", emoji: "✅", title: "Your Booking Is Confirmed!", subtitle: "We're all set for your moving day", text: "#1a1a1a" },
    subject: (c) => `Booking confirmed${c.move_date ? ` — ${fmtDate(c.move_date)}` : ""} | HomeGo Removals`,
    defaultMessage: (c) => `Hi ${firstName(c)},

Fantastic news — your move with HomeGo Removals is officially booked and confirmed!

Our professional crew will take great care of your belongings from start to finish. Please make sure someone is available at the property at the agreed time, and keep essentials (documents, valuables, medications) with you separately.

If anything changes before moving day, just reply to this email or give us a call — we're here to help.

We look forward to making your move stress-free!`,
  },
  review_request: {
    label: "Review Request — Job Complete",
    banner: { gradient: "linear-gradient(135deg, #D4AF37 0%, #f4d03f 50%, #D4AF37 100%)", emoji: "🏠✨", title: "Welcome to Your New Home!", subtitle: "We hope you're settling in beautifully", text: "#1a1a1a" },
    subject: (c) => `How was your move, ${firstName(c)}? — HomeGo Removals`,
    defaultMessage: (c) => `Hi ${firstName(c)},

Congratulations on your new home! It was a pleasure helping you with your move.

If you have a spare minute, we'd be incredibly grateful if you could share your experience in a quick review — it genuinely helps our small team grow and lets other families know they can trust us with their move.

Simply reply to this email and we'll send you the review link, or let us know if there's anything else we can help with.

Wishing you many happy memories in your new home!`,
  },
};

export const generateProManagerEmailHTML = ({ templateKey, customer, message, depositAmount }) => {
  const tpl = PM_EMAIL_TEMPLATES[templateKey] || PM_EMAIL_TEMPLATES.follow_up;
  const c = customer || {};
  const paragraphs = (message || "")
    .split("\n")
    .filter((p) => p.trim())
    .map((p) => `<p style="margin: 0 0 14px; font-family: Helvetica, Arial, sans-serif; font-size: 14px; color: #333; line-height: 1.7;">${p}</p>`)
    .join("");

  const detailRows = [
    c.quote_amount ? ["Quote Amount", `£${c.quote_amount}`] : null,
    c.package_selected ? ["Package", c.package_selected] : null,
    c.move_date ? ["Move Date", fmtDate(c.move_date)] : null,
    c.move_time ? ["Time", c.move_time] : null,
    (c.pickup_address || c.pickup_postcode) ? ["Moving From", c.pickup_address || c.pickup_postcode] : null,
    (c.delivery_address || c.delivery_postcode) ? ["Moving To", c.delivery_address || c.delivery_postcode] : null,
  ].filter(Boolean);

  const detailsBlock = detailRows.length ? `
          <tr>
            <td style="padding: 0 40px 10px;" class="mobile-padding">
              <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #FAFAF7; border: 1px solid #EDEAE0; border-radius: 12px;">
                <tr>
                  <td style="padding: 18px 22px;">
                    <p style="margin: 0 0 10px; font-family: Helvetica, Arial, sans-serif; font-size: 11px; font-weight: bold; color: #D4AF37; letter-spacing: 1.5px; text-transform: uppercase;">Your Move Details</p>
                    <table border="0" cellpadding="0" cellspacing="0" width="100%">
                      ${detailRows.map(([k, v]) => `
                      <tr>
                        <td style="padding: 5px 0; font-family: Helvetica, Arial, sans-serif; font-size: 13px; color: #888; width: 40%;">${k}</td>
                        <td style="padding: 5px 0; font-family: Helvetica, Arial, sans-serif; font-size: 13px; color: #1a1a1a; font-weight: bold;">${v}</td>
                      </tr>`).join("")}
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>` : "";

  const bankBlock = templateKey === "deposit_request" ? `
          <tr>
            <td style="padding: 10px 40px 10px;" class="mobile-padding">
              <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%); border-radius: 12px;">
                <tr>
                  <td style="padding: 22px 25px;">
                    <p style="margin: 0 0 4px; font-family: Helvetica, Arial, sans-serif; font-size: 11px; font-weight: bold; color: #D4AF37; letter-spacing: 1.5px; text-transform: uppercase;">💳 Deposit Payment — Bank Transfer</p>
                    ${depositAmount ? `<p style="margin: 0 0 14px; font-family: Helvetica, Arial, sans-serif; font-size: 26px; font-weight: bold; color: #FFFFFF;">£${depositAmount}</p>` : ""}
                    <table border="0" cellpadding="0" cellspacing="0" width="100%">
                      <tr><td style="padding: 4px 0; font-family: Helvetica, Arial, sans-serif; font-size: 13px; color: #999; width: 45%;">Account Name</td><td style="padding: 4px 0; font-family: Helvetica, Arial, sans-serif; font-size: 13px; color: #FFFFFF; font-weight: bold;">${BANK_DETAILS.name}</td></tr>
                      <tr><td style="padding: 4px 0; font-family: Helvetica, Arial, sans-serif; font-size: 13px; color: #999;">Account Type</td><td style="padding: 4px 0; font-family: Helvetica, Arial, sans-serif; font-size: 13px; color: #FFFFFF; font-weight: bold;">${BANK_DETAILS.type}</td></tr>
                      <tr><td style="padding: 4px 0; font-family: Helvetica, Arial, sans-serif; font-size: 13px; color: #999;">Account Number</td><td style="padding: 4px 0; font-family: Helvetica, Arial, sans-serif; font-size: 13px; color: #D4AF37; font-weight: bold;">${BANK_DETAILS.account}</td></tr>
                      <tr><td style="padding: 4px 0; font-family: Helvetica, Arial, sans-serif; font-size: 13px; color: #999;">Sort Code</td><td style="padding: 4px 0; font-family: Helvetica, Arial, sans-serif; font-size: 13px; color: #D4AF37; font-weight: bold;">${BANK_DETAILS.sort}</td></tr>
                      <tr><td style="padding: 4px 0; font-family: Helvetica, Arial, sans-serif; font-size: 13px; color: #999;">Reference</td><td style="padding: 4px 0; font-family: Helvetica, Arial, sans-serif; font-size: 13px; color: #FFFFFF; font-weight: bold;">${c.name || "Your Name"}</td></tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>` : "";

  return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>HomeGo Removals</title>
  <style type="text/css">
    @media only screen and (max-width: 620px) {
      .email-container { width: 100% !important; }
      .mobile-padding { padding-left: 15px !important; padding-right: 15px !important; }
    }
  </style>
</head>
<body style="margin: 0; padding: 0; background-color: #F4F4F5; font-family: Helvetica, Arial, sans-serif;">
  <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #F4F4F5;">
    <tr>
      <td align="center" style="padding: 20px 10px;">
        <table class="email-container" border="0" cellpadding="0" cellspacing="0" width="600" style="max-width: 600px; width: 100%; background-color: #FFFFFF; border-radius: 16px; overflow: hidden; box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);">
          <tr>
            <td style="background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%); padding: 30px 40px; text-align: center;">
              <h1 style="margin: 0; font-family: Helvetica, Arial, sans-serif; font-size: 32px; font-weight: bold; color: #FFFFFF; letter-spacing: 1px;">Home<span style="color: #D4AF37;">Go</span></h1>
              <p style="margin: 5px 0 0; font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #888; letter-spacing: 2px; text-transform: uppercase;">REMOVALS &amp; PACKING LTD</p>
            </td>
          </tr>
          <tr>
            <td style="background: ${tpl.banner.gradient}; padding: 30px 40px; text-align: center;">
              <p style="margin: 0; font-size: 44px; line-height: 1;">${tpl.banner.emoji}</p>
              <h2 style="margin: 12px 0 6px; font-family: Georgia, serif; font-size: 26px; font-weight: bold; color: ${tpl.banner.text};">${tpl.banner.title}</h2>
              <p style="margin: 0; font-family: Helvetica, Arial, sans-serif; font-size: 14px; color: #333;">${tpl.banner.subtitle}</p>
            </td>
          </tr>
          <tr>
            <td style="padding: 30px 40px 15px;" class="mobile-padding">
              ${paragraphs}
            </td>
          </tr>
${detailsBlock}
${bankBlock}
          <tr>
            <td style="padding: 15px 40px 30px;" class="mobile-padding">
              <table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: #FFFBEB; border: 1px solid #FDE68A; border-radius: 10px;">
                <tr>
                  <td style="padding: 14px 18px; text-align: center;">
                    <p style="margin: 0; font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #92400E; font-weight: bold;">🛡️ NO EXTRA CHARGES GUARANTEE — the price we quote is the price you pay</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="background: linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%); padding: 30px 40px; text-align: center;">
              <table border="0" cellpadding="0" cellspacing="0" width="100%">
                <tr>
                  <td align="center" style="padding-bottom: 20px;">
                    <table border="0" cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="padding: 0 6px;"><a href="https://wa.me/447440414548" style="display: inline-block; background: #25D366; color: #FFFFFF; font-family: Helvetica, Arial, sans-serif; font-size: 12px; font-weight: bold; text-decoration: none; padding: 10px 20px; border-radius: 25px;">💬 WhatsApp</a></td>
                        <td style="padding: 0 6px;"><a href="tel:03300434791" style="display: inline-block; background: #D4AF37; color: #000000; font-family: Helvetica, Arial, sans-serif; font-size: 12px; font-weight: bold; text-decoration: none; padding: 10px 20px; border-radius: 25px;">📞 Call Us</a></td>
                        <td style="padding: 0 6px;"><a href="mailto:office@homego-removals.com" style="display: inline-block; background: #3B82F6; color: #FFFFFF; font-family: Helvetica, Arial, sans-serif; font-size: 12px; font-weight: bold; text-decoration: none; padding: 10px 20px; border-radius: 25px;">✉️ Email</a></td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td align="center">
                    <p style="margin: 0; font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #FFFFFF;">Registered in England &amp; Wales | Company No: <span style="color: #D4AF37;">16811153</span></p>
                    <p style="margin: 8px 0 0; font-family: Helvetica, Arial, sans-serif; font-size: 11px; color: #888;">HomeGo Removals &amp; Packing Ltd | UK's Nationwide Premium Moving Company | 0330 043 4791</p>
                    <p style="margin: 15px 0 0; font-family: Helvetica, Arial, sans-serif; font-size: 10px; color: #555;">© HomeGo Removals &amp; Packing Ltd. All rights reserved.</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};
